import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '../lib/classnames.js';
import { useReducedMotion } from '../lib/useReducedMotion.js';

/**
 * CorridorScrub — the landing hero, rendered as a single cinematic <canvas>.
 *
 * It's a frame-sequence engine: it plays a sequence of photoreal corridor
 * renders (gpt-image-1.5, cached in R2) as one continuous glide down an opulent
 * gilded gallery toward a doorway of light. It works with as few as the current
 * six camera-stations (synthesising the in-between motion) or a dense extracted
 * sequence (indexing it directly) — the more frames it's given, the less it has
 * to invent.
 *
 * What makes it feel like film, not a slideshow:
 *  - PERPETUAL FORWARD DOLLY — the camera never fully stops; even between
 *    stations there's a continuous push, so the hall always feels walked.
 *  - CROSSFADE INTERPOLATION — adjacent frames smooth-dissolve, so there are no
 *    hard cuts regardless of how coarse the source sequence is.
 *  - AUTOPLAY ESTABLISHING SHOT — on load, a ~2.8s eased dolly-in plays before
 *    you touch anything, then settles into a slow ambient breath; the moment you
 *    scroll, it hands off (no jump) to scroll-driven walking.
 *  - BAKED GRADE — drifting film grain, a warm doorway bloom that grows toward
 *    the threshold, a breathing vignette, and a whisper of pointer parallax.
 *
 * Everything runs on one GPU-composited canvas in a single rAF loop, so it holds
 * 60fps on mobile. Overlays (headline, beats, CTA) are children, faded by the
 * parent via onProgress — which is reported from the SMOOTHED scrub position, so
 * the type stays locked to the picture. Reduced-motion holds the final still.
 */
export function CorridorScrub({
  images,
  heightVh = 460,
  className,
  children,
  onProgress,
}: {
  /** Backdrop render URLs, entrance → doorway order. */
  images: string[];
  heightVh?: number;
  className?: string;
  children?: ReactNode;
  onProgress?: (p: number) => void;
}) {
  const reduced = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;

  // Stable identity for the frame set, so the loader effect only re-runs when the
  // actual URLs change (not on every render).
  const srcKey = images.join('|');

  useEffect(() => {
    if (reduced) return;
    const canvas = canvasRef.current;
    const section = sectionRef.current;
    if (!canvas || !section || images.length === 0) return;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    // ---- decoded frames -------------------------------------------------
    interface Frame {
      src: CanvasImageSource;
      w: number;
      h: number;
    }
    const frames: (Frame | null)[] = images.map(() => null);
    let firstReady = -1;
    images.forEach((url, i) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        frames[i] = { src: img, w: img.naturalWidth || img.width, h: img.naturalHeight || img.height };
        if (firstReady === -1) firstReady = i;
      };
      img.src = url;
    });

    // ---- film grain tile (generated once) -------------------------------
    const grain = makeGrain(220);

    // ---- sizing (DPR-aware, capped for mobile fill-rate) ----------------
    let cw = 1;
    let ch = 1;
    let dpr = 1;
    const resize = (): void => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      cw = Math.max(1, Math.round(rect.width * dpr));
      ch = Math.max(1, Math.round(rect.height * dpr));
      canvas.width = cw;
      canvas.height = ch;
    };
    resize();

    // ---- progress + interaction state -----------------------------------
    let rawP = 0; // scroll progress through the tall section
    let dispP = 0; // smoothed, what the picture actually shows
    let engaged = false; // has the visitor taken control by scrolling?
    let parX = 0;
    let parY = 0;
    let parTX = 0;
    let parTY = 0;
    const fine = window.matchMedia('(pointer: fine)').matches;
    const start = performance.now();

    const measure = (): void => {
      const r = section.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      rawP = total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 0;
      // If the page loads already scrolled into the hero, take control at once.
      if (rawP > 0.01) engaged = true;
    };
    const engage = (): void => {
      engaged = true;
    };
    const onPointer = (e: PointerEvent): void => {
      if (!fine) return;
      parTX = (e.clientX / window.innerWidth) * 2 - 1;
      parTY = (e.clientY / window.innerHeight) * 2 - 1;
    };

    measure();

    // ---- the render loop ------------------------------------------------
    let raf = 0;
    let grainPhase = 0;
    const render = (now: number): void => {
      raf = requestAnimationFrame(render);

      // Smoothly chase the target: held at the entrance until the visitor
      // scrolls, then eased toward the live scroll position (premium inertia).
      const target = engaged ? rawP : 0;
      dispP += (target - dispP) * 0.1;
      const p = dispP;
      onProgressRef.current?.(p);

      const t = now - start;
      const intro = easeOutCubic(clamp01(t / 2800)); // establishing dolly-in
      parX += (parTX - parX) * 0.05;
      parY += (parTY - parY) * 0.05;

      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#0a0807';
      ctx.fillRect(0, 0, cw, ch);

      const n = frames.length;
      const pos = p * (n - 1);
      const i0 = Math.floor(pos);
      const i1 = Math.min(i0 + 1, n - 1);
      const frac = pos - i0;
      const f = smoothstep(frac);

      // Pick the under/over frames, falling back to whatever's decoded so the
      // hero never flashes black while later frames are still arriving.
      const fb = firstReady >= 0 ? frames[firstReady] : null;
      const a = frames[i0] ?? fb;
      const b = frames[i1] ?? a;

      if (a) {
        // STRICTLY MONOTONIC forward dolly — zoom is a pure function of the
        // (smoothed) scroll position, never decreasing.
        //
        // SINGLE-MASTER MODE (n === 1): the entire walk is a continuous
        // geometric zoom into ONE image, centred on the vanishing point. There
        // is NO crossfade between different renders — so the doorway is the same
        // pixels the whole way and the perspective stays perfectly registered.
        // A centre push-in on a one-point-perspective corridor reads as walking
        // straight down it. This is the only way to make the doorway and the
        // viewpoint perfectly coherent (generative frames each re-invent the far
        // doorway and never line up exactly across a crossfade).
        //
        // MULTI-FRAME MODE (n >= 2): a gentler zoom, with the crossfade carrying
        // the rest of the motion. Kept for future dense/coherent sequences.
        const single = n === 1;
        // DENSE-SEQUENCE MODE (n >= 24): the frames came from ONE continuous
        // video render (a real dolly extracted to a frame sequence) and already
        // carry the motion. Two things keep it SHARP and centred:
        //   - a NARROW crossfade: most of each segment shows a single crisp
        //     frame, blending only briefly at the boundary (a permanent
        //     two-frame dissolve is what read as blur);
        //   - a progressive RIGHTWARD nudge to re-centre the doorway, which the
        //     source dolly drifts left toward the end, with a touch of over-scale
        //     for the headroom that shift needs.
        const dense = n >= 24;
        let zoom: number;
        let dx: number;
        let dy: number;
        let dxFrac = 0; // horizontal shift as a FRACTION of the drawn image width
        let blend = f;
        if (single) {
          // One still → a big, perfectly-coherent geometric push-in.
          zoom = 1.02 + 0.8 * p + 0.05 * intro;
          dx = 0;
          dy = 0;
        } else if (dense) {
          zoom = 1.08 + 0.03 * intro;
          dx = 0;
          // Re-centre the doorway as a fraction of the IMAGE width — NOT canvas px
          // — so it lands centred on BOTH desktop (full-width crop) and mobile
          // (sides cropped), ramping in toward the threshold (p²). This clip's
          // door ends only ~1.5% left of centre, so the nudge is small (0.013);
          // a bigger value reads as right-heavy, especially on mobile.
          dxFrac = 0.013 * p * p;
          dy = 0;
          // FULL continuous crossfade (always dissolving toward the next frame):
          // motion stays smooth instead of holding-then-snapping, which is what
          // read as choppy/jumpy — and it masks the source's motion blur.
          blend = smoothstep(frac);
        } else {
          // A few coherent keyframes: crossfade + a gentle synthetic push.
          zoom = 1.05 + 0.12 * p + 0.05 * intro;
          const par = dpr * 10;
          dx = parX * par;
          dy = parY * par * 0.6;
        }
        drawCover(ctx, a, cw, ch, zoom, dx, dy, 1, dxFrac);
        if (b && b !== a) {
          drawCover(ctx, b, cw, ch, zoom, dx, dy, blend, dxFrac); // SAME zoom — no snap-back
        }
      }

      // Warm doorway bloom — grows toward the threshold, with a faint live flicker.
      const bloom = clamp01(0.12 + 0.9 * p) * (0.86 + 0.14 * Math.sin(t * 0.002));
      const cx = cw * 0.5;
      const cy = ch * 0.46;
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(cw, ch) * 0.62);
      g.addColorStop(0, `rgba(255,216,150,${(0.11 * bloom).toFixed(3)})`);
      g.addColorStop(0.42, `rgba(255,190,110,${(0.05 * bloom).toFixed(3)})`);
      g.addColorStop(1, 'rgba(255,180,90,0)');
      ctx.globalCompositeOperation = 'screen';
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, cw, ch);

      // God-rays — shafts of light spilling from the doorway. Cost scales with
      // shaft count × per-shaft animated gradient. On coarse-pointer devices
      // (mobile), halve the shaft count and drop the per-shaft breathing
      // animation — same effect visually, ~4× cheaper per frame. This was
      // the second-biggest RAF cost after the film grain on iOS.
      const rayStrength = clamp01((p - 0.12) / 0.88);
      if (rayStrength > 0.01) {
        const ry = ch * 0.42;
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.translate(cx, ry);
        const drift = fine ? Math.sin(t * 0.00013) * 0.16 : 0;
        const shafts = fine ? 6 : 3;
        const len = Math.max(cw, ch) * 1.25;
        for (let s = 0; s < shafts; s++) {
          const ang = drift + (s - (shafts - 1) / 2) * (fine ? 0.17 : 0.28);
          ctx.save();
          ctx.rotate(ang);
          const halfW = cw * (fine ? 0.02 + 0.012 * Math.sin(t * 0.0007 + s) : 0.024);
          const a = fine
            ? (0.055 + 0.022 * Math.sin(t * 0.0011 + s * 1.7)) * rayStrength
            : 0.065 * rayStrength;
          const rg = ctx.createLinearGradient(0, 0, 0, len);
          rg.addColorStop(0, `rgba(255,228,170,${a.toFixed(3)})`);
          rg.addColorStop(0.5, `rgba(255,212,145,${(a * 0.45).toFixed(3)})`);
          rg.addColorStop(1, 'rgba(255,200,120,0)');
          ctx.fillStyle = rg;
          ctx.beginPath();
          ctx.moveTo(-halfW * 0.32, 0);
          ctx.lineTo(halfW * 0.32, 0);
          ctx.lineTo(halfW * 1.6, len);
          ctx.lineTo(-halfW * 1.6, len);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
        ctx.restore();
      }

      // Anamorphic lens streak — a wide, cool-tinted horizontal flare bisecting the
      // doorway light. The blue fringe + warm core is the classic anamorphic tell;
      // it widens toward the threshold so the lens feels like it's drinking light.
      const streakA = (0.05 + 0.03 * Math.sin(t * 0.0017)) * clamp01((p - 0.2) / 0.8);
      if (streakA > 0.002) {
        const sy = ch * 0.42;
        const sg = ctx.createLinearGradient(0, sy, cw, sy);
        sg.addColorStop(0, 'rgba(120,170,255,0)');
        sg.addColorStop(0.34, `rgba(150,185,255,${(streakA * 0.5).toFixed(3)})`);
        sg.addColorStop(0.5, `rgba(255,242,212,${streakA.toFixed(3)})`);
        sg.addColorStop(0.66, `rgba(150,185,255,${(streakA * 0.5).toFixed(3)})`);
        sg.addColorStop(1, 'rgba(120,170,255,0)');
        ctx.globalCompositeOperation = 'screen';
        ctx.fillStyle = sg;
        const sh = ch * (0.016 + 0.02 * p);
        ctx.fillRect(0, sy - sh / 2, cw, sh);
      }

      // Terminal light flood — the payoff. As you reach the doorway the threshold
      // blooms warm and floods the hall. It sits BEHIND the sign-in card (canvas is
      // z-0, the card z-10 with its own dark face), so it never washes the type.
      const flood = clamp01((p - 0.82) / 0.18);
      if (flood > 0.002) {
        const fa = flood * flood;
        const fg = ctx.createRadialGradient(cx, ch * 0.44, 0, cx, ch * 0.44, Math.max(cw, ch) * 0.78);
        fg.addColorStop(0, `rgba(255,242,208,${(0.26 * fa).toFixed(3)})`);
        fg.addColorStop(0.5, `rgba(255,222,165,${(0.11 * fa).toFixed(3)})`);
        fg.addColorStop(1, 'rgba(255,210,140,0)');
        ctx.globalCompositeOperation = 'screen';
        ctx.fillStyle = fg;
        ctx.fillRect(0, 0, cw, ch);
      }

      // Cursor torchlight — a soft warm glow that follows the pointer, so moving
      // the mouse feels like a light catching the gilt. Desktop (fine pointer) only.
      if (fine) {
        const gx = (parX * 0.5 + 0.5) * cw;
        const gy = (parY * 0.5 + 0.5) * ch;
        const gr = Math.max(cw, ch) * 0.3;
        const cg = ctx.createRadialGradient(gx, gy, 0, gx, gy, gr);
        cg.addColorStop(0, 'rgba(255,212,150,0.10)');
        cg.addColorStop(1, 'rgba(255,190,110,0)');
        ctx.globalCompositeOperation = 'screen';
        ctx.fillStyle = cg;
        ctx.fillRect(0, 0, cw, ch);
      }

      // Vignette — static, pulls the eye down the hall and keeps edges rich.
      // (No pulsing: any periodic change reads as drift on a slow scroll.)
      const vg = ctx.createRadialGradient(cx, ch * 0.44, ch * 0.1, cx, ch * 0.5, Math.max(cw, ch) * 0.72);
      vg.addColorStop(0, 'rgba(0,0,0,0)');
      vg.addColorStop(0.66, 'rgba(0,0,0,0)');
      vg.addColorStop(1, 'rgba(0,0,0,0.5)');
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, cw, ch);

      // Chromatic aberration — SKIPPED on coarse-pointer devices. The paired
      // radial fills are cheap individually but they run every frame; on iOS
      // the pixel-fill of two full-canvas radials is meaningful. The lens
      // fringe reads only when you're staring, which no one does on a phone.
      if (fine) {
        const caR = ctx.createRadialGradient(cx, ch * 0.5, Math.min(cw, ch) * 0.5, cx, ch * 0.5, Math.max(cw, ch) * 0.66);
        caR.addColorStop(0, 'rgba(0,0,0,0)');
        caR.addColorStop(0.8, 'rgba(0,0,0,0)');
        caR.addColorStop(1, 'rgba(120,40,90,0.07)');
        ctx.globalCompositeOperation = 'screen';
        ctx.fillStyle = caR;
        ctx.fillRect(0, 0, cw, ch);
        const caB = ctx.createRadialGradient(cx, ch * 0.5, Math.min(cw, ch) * 0.42, cx, ch * 0.5, Math.max(cw, ch) * 0.58);
        caB.addColorStop(0, 'rgba(0,0,0,0)');
        caB.addColorStop(0.82, 'rgba(0,0,0,0)');
        caB.addColorStop(1, 'rgba(40,90,140,0.06)');
        ctx.fillStyle = caB;
        ctx.fillRect(0, 0, cw, ch);
      }

      // Drifting film grain — SKIPPED on coarse-pointer devices. drawImage of
      // a 220px texture blended at overlay every other frame is the single
      // most expensive op in the loop; on iOS Safari it was ~40% of the RAF
      // budget. Grain adds cinematic feel but a phone screen can't resolve it
      // at the density the desktop can — no visible loss.
      if (fine) {
        grainPhase ^= 1;
        if (grainPhase === 0) {
          ctx.globalCompositeOperation = 'overlay';
          ctx.globalAlpha = 0.05;
          const gw = cw * 1.16;
          const gh = ch * 1.16;
          const ox = -cw * 0.08 - Math.random() * cw * 0.04;
          const oy = -ch * 0.08 - Math.random() * ch * 0.04;
          ctx.drawImage(grain, ox, oy, gw, gh);
          ctx.globalAlpha = 1;
          ctx.globalCompositeOperation = 'source-over';
        }
      }
    };

    raf = requestAnimationFrame(render);
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('wheel', engage, { passive: true });
    window.addEventListener('touchmove', engage, { passive: true });
    window.addEventListener('keydown', engage);
    if (fine) window.addEventListener('pointermove', onPointer, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', resize);
      window.removeEventListener('resize', measure);
      window.removeEventListener('wheel', engage);
      window.removeEventListener('touchmove', engage);
      window.removeEventListener('keydown', engage);
      window.removeEventListener('pointermove', onPointer);
    };
    // `images` is intentionally tracked via srcKey (stable per URL set), not by
    // reference, so the loop isn't torn down on unrelated parent re-renders.
  }, [reduced, srcKey]);

  if (images.length === 0) return null;

  // Reduced motion: hold the final (doorway) still under the overlays.
  if (reduced) {
    return (
      <section className={cn('relative', className)} style={{ position: 'relative' }}>
        <div style={{ position: 'relative', height: '100vh', width: '100%', overflow: 'hidden' }}>
          <img
            src={images[images.length - 1]}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
          <Scrim />
          {children}
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className={cn('relative', className)}
      style={{ height: `${heightVh}vh`, position: 'relative', backgroundColor: '#0a0807' }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          width: '100%',
          overflow: 'hidden',
          backgroundColor: '#0a0807',
        }}
      >
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full"
        />
        <Scrim />
        {children}
      </div>
    </section>
  );
}

/** Cinematic vignette + top/bottom gradient so overlaid type always reads. */
function Scrim() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-[5]"
      style={{
        background:
          'radial-gradient(125% 95% at 50% 42%, transparent 30%, rgba(0,0,0,0.34) 72%, rgba(0,0,0,0.7) 100%),' +
          'linear-gradient(180deg, rgba(0,0,0,0.52) 0%, rgba(0,0,0,0.12) 24%, rgba(0,0,0,0.12) 50%, rgba(0,0,0,0.84) 100%)',
      }}
    />
  );
}

/* ------------------------------------------------------------------ helpers */

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
function easeOutCubic(v: number): number {
  const x = 1 - v;
  return 1 - x * x * x;
}
function smoothstep(v: number): number {
  const x = clamp01(v);
  return x * x * (3 - 2 * x);
}

/** Draw a frame cover-fit with an extra zoom + offset and a crossfade alpha. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  frame: { src: CanvasImageSource; w: number; h: number },
  cw: number,
  ch: number,
  zoom: number,
  dx: number,
  dy: number,
  alpha: number,
  dxFrac = 0,
): void {
  const { src, w: iw, h: ih } = frame;
  if (!iw || !ih) return;
  const scale = Math.max(cw / iw, ch / ih) * zoom;
  const w = iw * scale;
  const h = ih * scale;
  // dxFrac shifts horizontally by a fraction of the DRAWN width (viewport-
  // independent — centres the doorway the same on mobile and desktop).
  const x = (cw - w) / 2 + dx + dxFrac * w;
  const y = (ch - h) / 2 + dy;
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(src, x, y, w, h);
  ctx.globalAlpha = 1;
}

/** A small offscreen monochrome-noise tile, drawn over the frame as film grain. */
function makeGrain(size: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const g = c.getContext('2d');
  if (g) {
    const img = g.createImageData(size, size);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const v = (Math.random() * 255) | 0;
      d[i] = v;
      d[i + 1] = v;
      d[i + 2] = v;
      d[i + 3] = 255;
    }
    g.putImageData(img, 0, 0);
  }
  return c;
}
